/**
 * ⚠️ TEMPORARY — DELETE ME.
 *
 * This module exists only to stand up the board off live data without checking in a
 * production fixture. It maps a `RepoIssuesDashboardContentResponse` list-view payload
 * (proto-JSON returned by `GET /:owner/:repo/issues`) into the board's
 * `BoardInitialPayload` contract, faking column grouping client-side by label.
 *
 * It goes away the moment the board has its own REST endpoint that returns the payload
 * shape directly. Do not extend this file with new features, new grouping strategies,
 * or new callers — anything you're tempted to add here belongs in that endpoint.
 */

import type {
  RepoIssue,
  RepoIssueAssignee,
  RepoIssueMilestone,
  RepoIssuePullRequest,
  RepoIssuePullRequestAssignee,
  RepoIssuesDashboardLabel,
  RepoIssuesDashboardResult,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {
  RepoIssuePullRequestDisplayState,
  RepoIssueState,
  RepoIssueStateReason,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import type {SafeHtml} from '@github-ui/dotcom-schema/types/common/v1/safe_html'

import type {
  BoardInitialPayload,
  BoardPayloadAssignee,
  BoardPayloadCell,
  BoardPayloadColumn,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadMilestone,
  IssueStateReason,
  PullRequestState,
} from './board-payload'
import {
  Capability,
  FieldIdentifier,
  ISSUE,
  PULL_REQUEST,
  type ViewDefinition,
} from '../../saved-view-options/view-definition'

/**
 * Hardcoded stand-in for the view definition the board endpoint will eventually return.
 * Colocated with the mapper because both are temporary fakes — they go away together when
 * the endpoint lands, at which point `ViewDefinitionProvider` fetches and validates
 * against `ViewDefinitionSchema`. The listed fields must stay in sync with the attribute
 * gates in `BoardCard.tsx`.
 */
export const TEMP_VIEW_DEFINITION_PAYLOAD: ViewDefinition = {
  itemKinds: [ISSUE, PULL_REQUEST],
  fields: [
    {
      identifier: FieldIdentifier.LABEL,
      label: 'Label',
      icon: 'tag',
      capabilities: {[Capability.GROUPABLE]: [ISSUE, PULL_REQUEST], [Capability.ATTRIBUTABLE]: [ISSUE, PULL_REQUEST]},
    },
    {
      identifier: FieldIdentifier.MILESTONE,
      label: 'Milestone',
      icon: 'milestone',
      capabilities: {[Capability.ATTRIBUTABLE]: [ISSUE, PULL_REQUEST]},
    },
    {
      identifier: FieldIdentifier.ASSIGNEE,
      label: 'Assignees',
      icon: 'person',
      capabilities: {[Capability.ATTRIBUTABLE]: [ISSUE, PULL_REQUEST]},
    },
  ],
}

/**
 * Bucket key used for items that carry no labels. Rendered as `"No label"` on the axis.
 */
const NO_LABEL_KEY = '__no_label__'

/** What the {@link NO_LABEL_KEY} column is called on the axis. */
const NO_LABEL_COLUMN_NAME = 'No label'

/**
 * A label's column key.
 *
 * Prefixed because the axis also carries the synthetic {@link NO_LABEL_KEY} bucket: keyed
 * by bare name, a repository label called `__no_label__` lands in that bucket, merging its
 * items with the unlabelled ones under a heading that names neither group.
 */
function labelColumnKey(labelName: string): string {
  return `label:${labelName}`
}

/**
 * The label a column was bucketed from.
 *
 * `nameHtml` stays the server's `SafeHtml` message rather than being flattened to a
 * string: a label name is user-controlled, and only the server-rendered field may reach an
 * HTML sink. It is set only when there is markup to draw, so a column whose response
 * carried none is named by its plain text instead.
 */
export type BoardColumnLabel = {
  name: string
  nameHtml: SafeHtml | undefined
  color: string
}

/**
 * The label each column was bucketed from, keyed by column key. The `"No label"` column
 * has no entry, because no label is what it stands for.
 *
 * The board's `BoardColumn` carries a key, a name and a count, which is everything a
 * board needs and less than a label heading draws. The grouping is done here and the
 * labels are already in hand, so the metadata comes back from the same pass rather than
 * being searched for again in the items.
 */
export type BoardColumnLabels = ReadonlyMap<string, BoardColumnLabel>

export type BoardPayloadWithColumnLabels = {
  payload: BoardInitialPayload
  columnLabels: BoardColumnLabels
}

/** One column's items, and the label they were bucketed from -- `null` for `"No label"`. */
type ColumnBucket = {
  label: BoardColumnLabel | null
  items: BoardPayloadItem[]
}

/**
 * Maps the list-view search response into a {@link BoardInitialPayload} grouped by label.
 *
 * The board is single-axis for now (no swimlane; `rowField: null`). Every item is placed
 * fall into the `"No label"` bucket. Cells advertise `items.length` as their `totalCount`
 * only because paging isn't wired yet (github/views#139).
 */
export function resultsToBoardPayload(results: readonly RepoIssuesDashboardResult[]): BoardPayloadWithColumnLabels {
  const items: BoardPayloadItem[] = []
  const bucketsByKey = new Map<string, ColumnBucket>()

  for (const result of results) {
    const item = toBoardItem(result)
    if (item === null) continue
    items.push(item)

    const labels = resultLabels(result)
    if (labels.length === 0) {
      appendItem(bucketsByKey, NO_LABEL_KEY, null, item)
      continue
    }
    for (const label of labels) {
      appendItem(bucketsByKey, labelColumnKey(label.name), readColumnLabel(label), item)
    }
  }

  const columns: BoardPayloadColumn[] = []
  const cells: BoardPayloadCell[] = []
  const columnLabels = new Map<string, BoardColumnLabel>()
  for (const [key, bucket] of bucketsByKey) {
    columns.push({key, label: bucket.label?.name ?? NO_LABEL_COLUMN_NAME, totalCount: bucket.items.length})
    if (bucket.label !== null) columnLabels.set(key, bucket.label)
    cells.push({
      columnKey: key,
      rowKey: 'all',
      totalCount: bucket.items.length,
      itemsLoaded: true,
      hasMoreItems: false,
      itemsCursor: null,
      items: bucket.items,
    })
  }

  return {
    payload: {
      columns,
      rows: [{key: 'all', label: '', totalCount: items.length}],
      columnField: 'label',
      rowField: null,
      countsOverlap: true,
      cells,
      hasMoreColumns: false,
      hasMoreRows: false,
      columnsTruncated: false,
      rowsTruncated: false,
      cellsTruncated: false,
      columnsCursor: null,
      rowsCursor: null,
    },
    columnLabels,
  }
}

function appendItem(
  buckets: Map<string, ColumnBucket>,
  key: string,
  label: BoardColumnLabel | null,
  item: BoardPayloadItem,
): void {
  const existing = buckets.get(key)
  if (existing) {
    existing.items.push(item)
  } else {
    buckets.set(key, {label, items: [item]})
  }
}

function resultLabels(result: RepoIssuesDashboardResult): readonly RepoIssuesDashboardLabel[] {
  if (result.item.case === 'issue' || result.item.case === 'pullRequest') return result.item.value.labels
  return []
}

function readColumnLabel(label: RepoIssuesDashboardLabel): BoardColumnLabel {
  return {
    name: label.name,
    // Dropped when it holds nothing, so that a present `nameHtml` always means markup to
    // draw and a heading is never rendered blank.
    nameHtml: label.nameHtml?.value ? label.nameHtml : undefined,
    color: label.color ?? '',
  }
}

function toBoardItem(result: RepoIssuesDashboardResult): BoardPayloadItem | null {
  if (result.item.case === 'issue') return toIssueItem(result.item.value, result.id)
  if (result.item.case === 'pullRequest') return toPullRequestItem(result.item.value, result.id)
  return null
}

function toIssueItem(issue: RepoIssue, globalId: string): BoardPayloadItem {
  return {
    type: 'issue',
    // The proto `id` is the global id; the payload's numeric `id` isn't read by the card.
    id: 0,
    globalId,
    number: issue.number,
    title: issue.title,
    titleHtml: issue.titleHtml?.value ?? issue.title,
    permalink: issue.permalink ?? '',
    repoNameWithOwner: issue.repoNameWithOwner ?? '',
    state: issue.state === RepoIssueState.CLOSED ? 'closed' : 'open',
    stateReason: mapIssueStateReason(issue.stateReason),
    fields: {
      label: {value: issue.labels.map(readLabel)},
      milestone: {value: readMilestone(issue.milestone)},
      assignee: {value: issue.assignees.map(readIssueAssignee)},
    },
  }
}

function toPullRequestItem(pull: RepoIssuePullRequest, globalId: string): BoardPayloadItem {
  return {
    type: 'pull_request',
    id: 0,
    globalId,
    number: pull.number,
    title: pull.title,
    titleHtml: pull.titleHtml?.value ?? pull.title,
    permalink: pull.permalink ?? '',
    repoNameWithOwner: pull.repoNameWithOwner ?? '',
    state: mapPullRequestState(pull),
    fields: {
      label: {value: pull.labels.map(readLabel)},
      milestone: {value: null},
      assignee: {value: pull.assignees.map(readPullRequestAssignee)},
    },
  }
}

function mapIssueStateReason(reason: RepoIssueStateReason): IssueStateReason | null {
  switch (reason) {
    case RepoIssueStateReason.COMPLETED:
      return 'completed'
    case RepoIssueStateReason.NOT_PLANNED:
      return 'not_planned'
    case RepoIssueStateReason.REOPENED:
      return 'reopened'
    case RepoIssueStateReason.DUPLICATE:
      return 'duplicate'
    default:
      return null
  }
}

function mapPullRequestState(pull: RepoIssuePullRequest): PullRequestState {
  // `displayState` folds draft/queued into the same enum the card renders on.
  switch (pull.displayState) {
    case RepoIssuePullRequestDisplayState.DRAFT:
      return 'draft'
    case RepoIssuePullRequestDisplayState.MERGED:
      return 'merged'
    case RepoIssuePullRequestDisplayState.CLOSED:
      return 'closed'
    case RepoIssuePullRequestDisplayState.QUEUED:
      return 'queued'
    default:
      return 'open'
  }
}

function readLabel(label: RepoIssuesDashboardLabel): BoardPayloadLabel {
  return {
    // Proto `id` is a global string; the payload numeric `id` isn't read by the card.
    id: 0,
    name: label.name,
    // Empty rather than the plain name when the response omits the server-rendered field:
    // the name is user-controlled, and the card renders this one as HTML. Consumers fall
    // back to `name` as text.
    nameHtml: label.nameHtml?.value ?? '',
    color: label.color ?? '',
    description: label.description ?? null,
  }
}

function readMilestone(milestone: RepoIssueMilestone | undefined): BoardPayloadMilestone | null {
  if (!milestone) return null
  // The list-view proto only carries `title` + `url`. `state` and `dueOn` aren't wired
  // on this endpoint; default to open/null until the board endpoint delivers them.
  return {title: milestone.title, url: milestone.url, state: 'open', dueOn: null}
}

function readIssueAssignee(assignee: RepoIssueAssignee): BoardPayloadAssignee {
  return {
    login: assignee.login,
    name: assignee.name ?? null,
    avatarUrl: assignee.avatarUrl,
    isAgent: assignee.isAgent,
    isCopilot: assignee.isCopilot,
  }
}

function readPullRequestAssignee(assignee: RepoIssuePullRequestAssignee): BoardPayloadAssignee {
  // Pull-request assignee proto is a minimal `{login, avatarUrl}` — no agent flag or name.
  return {login: assignee.login, name: null, avatarUrl: assignee.avatarUrl, isAgent: false, isCopilot: false}
}
